import { type NavigationGuardWithThis } from 'vue-router'
import { canAccessFeature, type FeatureFlag } from '@/vue/composables/use-can-access-feature'
import type { Permission } from '@/vue/helpers/permissions'
import { useBreadcrumbsStore } from '@/vue/stores/breadcrumbsStore'
import { useUserStore } from '@/vue/stores/userStore'

/**
 * Navigation guard to check if user has permission to access a route and if the feature is not disabled for him.
 * Plays together with the canEnterRoute function in ./canEnter.ts.
**/
export function check(
    permissions: Permission[],
    orRedirectTo: string,
    feature?: FeatureFlag,
): NavigationGuardWithThis<undefined> {
    return async (_, __, next) => {
        const userStore = useUserStore()
        await userStore.fetchUser(false)

        // After fetch, if userData is still null, auth likely failed
        // Axios interceptor will handle redirect to login - just abort this navigation
        // to prevent racing to dashboard before the page reload happens
        if (!userStore.userData) {
            next(false)
            return
        }

        const hasPermission = permissions.length === 0 || permissions.some(permission => userStore.hasPermission(permission))
        const canEnter = !feature || canAccessFeature(feature)

        if (hasPermission && canEnter) {
            next()
        } else {
            // @ts-expect-error baseline
            next({ name: orRedirectTo })
        }
    }
}

/**
 * Navigation guard that allows access if user has any of the given permissions OR is a consultant admin
 * for the current company context.
 */
export function checkWithConsultantAdmin(
    permissions: Permission[],
    orRedirectTo: string,
    feature?: FeatureFlag,
): NavigationGuardWithThis<undefined> {
    return async (_, __, next) => {
        const userStore = useUserStore()
        await userStore.fetchUser(false)

        if (!userStore.userData) {
            next(false)
            return
        }

        const hasPermission = permissions.length === 0 || permissions.some(permission => userStore.hasPermission(permission))
        const breadcrumbsStore = useBreadcrumbsStore()
        const companyId = breadcrumbsStore.companyId
        const isConsultantAdmin = companyId !== null && userStore.isConsultantAdminFor(companyId)
        const canEnter = !feature || canAccessFeature(feature)

        if ((hasPermission || isConsultantAdmin) && canEnter) {
            next()
        } else {
            // @ts-expect-error baseline
            next({ name: orRedirectTo })
        }
    }
}
